import { transformForSubmit } from 'platform/forms-system/src/js/helpers';
import { format } from 'date-fns-tz';
import {
  // calculateSeparationDuration,
  splitVaSsnField,
  switchToInternationalPhone,
  updateBankValues,
  transformCareExpenses,
  combineTreatmentFacility,
  combineUnitNameAddress,
  buildUnitAddress,
  // chapter4Transform,
  checkForHowMarriageEnded,
  transformClaim,
  autoPopulateMarriageEndDate,
  transformPreviousMarriages,
  addPayerNameForSocialSecurity,
  renameOtherIncomeType,
  transformMedicalExpenses,
  transformClaimant,
} from '../../utils/transformers';
import {
  chapter4Transform,
  calculateSeparationDuration,
} from '../utils/transformers';
import { buildChildStatus } from '../../utils/transformers/helpers';
import { isRelationshipToVeteran } from './utils/helpers';

import { reconcileCaveArtifacts } from '../../cave';

// Prefilled, display-only identity keys owned only by the legacy claimant pages,
// which are inactive under survivorsBenefitsUpdatedPrefill.
const PREFILLED_IDENTITY_KEYS = [
  'yourName',
  'yourDateOfBirth',
  'yourSocialSecurityNumber',
];

// Snapshot the prefilled identity values before transformForSubmit runs, because
// filterInactivePageData mutates form.data in place (delete newData[prop]) and
// removes these inactive-page keys from the original object.
const snapshotPrefilledIdentity = formData => {
  const snapshot = {};
  if (!formData) return snapshot;
  PREFILLED_IDENTITY_KEYS.forEach(key => {
    if (formData[key] !== undefined) {
      snapshot[key] = formData[key];
    }
  });
  return snapshot;
};

const restorePrefilledIdentity = (transformedData, snapshot) => {
  const parsed = JSON.parse(transformedData);
  Object.keys(snapshot).forEach(key => {
    if (parsed[key] === undefined) {
      parsed[key] = snapshot[key];
    }
  });
  return JSON.stringify(parsed);
};

// A surviving child answers 2B(3)/2B(4) in "Your information". vets-api reads
// those boxes and Part E from the first veteransChildren entry, so the
// claimant goes first, with a status built the same way as household children.
export const buildClaimantChild = formData => ({
  childFullName: formData.claimantFullName,
  childDateOfBirth: formData.claimantDateOfBirth,
  childSocialSecurityNumber: formData.claimantSocialSecurityNumber,
  childRelationshipToVeteran: formData.childRelationshipToVeteran,
  childStatus: buildChildStatus({
    relationship: formData.childRelationshipToVeteran,
    inSchool: formData.childInSchool,
    seriouslyDisabled: formData.childSeriouslyDisabled,
    hasBeenMarried: formData.childPreviouslyMarried,
  }),
});

export const addClaimantChild = formData => {
  if (isRelationshipToVeteran(formData) !== 'child') return formData;
  return {
    ...formData,
    veteransChildren: [
      buildClaimantChild(formData),
      ...(formData.veteransChildren || []),
    ],
  };
};

export const transform = (formConfig, form) => {
  // Check if feature flag for combining unit name and address is enabled
  const use2025Version = form?.data?.survivorsBenefitsForm2025VersionEnabled;

  // Capture identity keys before transformForSubmit mutates form.data.
  const identitySnapshot = snapshotPrefilledIdentity(form?.data);
  let transformedData = transformForSubmit(formConfig, form);
  // Restore prefilled identity keys that transformForSubmit drops as
  // inactive-page data: under survivorsBenefitsUpdatedPrefill their legacy
  // owner pages are inactive and the display-only personal-info page declares
  // no schema, so they must be re-applied before transformClaimant maps them
  // to claimant*.
  transformedData = restorePrefilledIdentity(transformedData, identitySnapshot);
  // Fold CAVE conflict-resolution corrections (name, SSN, DOB, branch, service dates) into
  // files[].idpArtifacts before splitVaSsnField flattens veteranSocialSecurityNumber, so
  // downstream idpArtifacts consumers (21-4138 change log, MMS, BPDS) get the user-final values.
  transformedData = reconcileCaveArtifacts(transformedData);
  transformedData = transformClaimant(transformedData);
  transformedData = calculateSeparationDuration(transformedData);
  transformedData = splitVaSsnField(transformedData);
  transformedData = switchToInternationalPhone(transformedData);
  transformedData = updateBankValues(transformedData);
  transformedData = renameOtherIncomeType(transformedData);
  transformedData = addPayerNameForSocialSecurity(transformedData);
  transformedData = transformCareExpenses(transformedData);
  transformedData = transformMedicalExpenses(transformedData);
  transformedData = combineTreatmentFacility(transformedData);

  // Use feature flag to determine which transformer to apply
  transformedData = use2025Version
    ? buildUnitAddress(transformedData)
    : combineUnitNameAddress(transformedData);

  transformedData = autoPopulateMarriageEndDate(transformedData);
  transformedData = transformPreviousMarriages(transformedData);
  transformedData = chapter4Transform(transformedData);
  transformedData = checkForHowMarriageEnded(transformedData);
  transformedData = transformClaim(transformedData);
  transformedData = JSON.stringify(
    addClaimantChild(JSON.parse(transformedData)),
  );
  return JSON.stringify({
    survivorsBenefitsClaim: {
      form: transformedData,
    },
    localTime: format(new Date(), "yyyy-MM-dd'T'HH:mm:ssXXX"),
  });
};
